import axios, { AxiosResponse } from 'axios';
import {
  User,
  LoginRequest,
  GoogleLoginRequest,
  GoogleCountrySelectionRequest,
  GoogleCountrySelectionRequired,
  RegisterRequest,
  TokenResponse,
  UserCreateRequest,
  UserUpdateRequest,
  UserFilters,
  Product,
  ProductFormData,
  ProductFilters,
  ProductImportResult,
  PriceList,
  PriceListEntry,
  PriceListEntryFormData,
  PriceListEntryResult,
  PriceListImportResult,
  FreightRate,
  FreightRateFormData,
  InstallationService,
  InstallationServiceCreate,
  CatalogImportRun,
  Budget,
  BudgetVersion,
  BudgetFormData,
  BudgetFilters,
  PaginatedResponse,
  ApiError,
  ErpClienteCreateRequest,
  ErpSubmitResult,
  ErpSubmitStatus,
  MlOrder
} from '../types';

// Configuración base de axios
const API_BASE_URL = process.env.REACT_APP_API_URL;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Permitir envío de cookies
});

// Variables para controlar el refresh de tokens
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Interceptor para manejar errores
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class ApiService {
  /**
   * Crea una nueva versión del presupuesto con los datos y items actuales
   */
  createVersion = async (id: number, versionData: any): Promise<any> => {
    const response = await axiosInstance.post(`/budgets/${id}/versions`, versionData);
    return response.data;
  };
  // Estadísticas de productos
  productStats = {
    getMostUsed: async (limit: number = 10): Promise<any[]> => {
      const response: AxiosResponse<any[]> = await axiosInstance.get(`/products/stats/most-used`, { params: { limit } });
      return response.data;
    },
    getWithoutStock: async (limit: number = 50): Promise<any[]> => {
      const response: AxiosResponse<any[]> = await axiosInstance.get(`/products/stats/without-stock`, { params: { limit } });
      return response.data;
    },
    getInactive: async (limit: number = 50): Promise<any[]> => {
      const response: AxiosResponse<any[]> = await axiosInstance.get(`/products/stats/inactive`, { params: { limit } });
      return response.data;
    },
  };
  // Configurar token de autorización (se mantiene en memoria, no en localStorage)
  setAuthToken(token: string | null) {
    if (token) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.common['Authorization'];
    }
  }

  // Métodos genéricos HTTP
  async get<T>(url: string, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.get(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.put(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.patch(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: any): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.delete(url, config);
    return response.data;
  }

  // Endpoints de autenticación
  auth = {
    login: async (credentials: LoginRequest): Promise<TokenResponse> => {
      const response: AxiosResponse<TokenResponse> = await axiosInstance.post('/auth/login', credentials);
      return response.data;
    },

    // Phase 1B (CLAUDE.md §1/§2): can come back as a token OR, for the rare
    // dual-account case, `{requires_country_selection: true, options: [...]}`
    // - see types.ts's isCountrySelectionRequired.
    loginWithGoogle: async (
      googleLogin: GoogleLoginRequest
    ): Promise<TokenResponse | GoogleCountrySelectionRequired> => {
      const response: AxiosResponse<TokenResponse | GoogleCountrySelectionRequired> =
        await axiosInstance.post('/auth/google', googleLogin);
      return response.data;
    },

    selectCountryAfterGoogleLogin: async (
      payload: GoogleCountrySelectionRequest
    ): Promise<TokenResponse> => {
      const response: AxiosResponse<TokenResponse> = await axiosInstance.post(
        '/auth/google/select-country', payload
      );
      return response.data;
    },

    register: async (userData: RegisterRequest): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.post('/auth/register', userData);
      return response.data;
    },

    getCurrentUser: async (): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.get('/auth/me');
      return response.data;
    },

    logout: async (): Promise<{message: string}> => {
      const response: AxiosResponse<{message: string}> = await axiosInstance.post('/auth/logout', {});
      return response.data;
    },
  };

  // Endpoints de usuarios (admin only)
  users = {
    getAll: async (filters: UserFilters = {}): Promise<User[]> => {
      const response: AxiosResponse<User[]> = await axiosInstance.get('/users', {
        params: filters
      });
      return response.data;
    },

    getById: async (id: number): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.get(`/users/${id}`);
      return response.data;
    },

    create: async (userData: UserCreateRequest): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.post('/users', userData);
      return response.data;
    },

    update: async (id: number, userData: UserUpdateRequest): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.put(`/users/${id}`, userData);
      return response.data;
    },

    delete: async (id: number): Promise<{message: string}> => {
      const response: AxiosResponse<{message: string}> = await axiosInstance.delete(`/users/${id}`);
      return response.data;
    },

    updateProfile: async (userData: UserUpdateRequest): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.put('/users/me', userData);
      return response.data;
    },

    changePassword: async (userId: number, newPassword: string): Promise<{message: string}> => {
      const response: AxiosResponse<{message: string}> = await axiosInstance.post(
        `/users/${userId}/change-password`,
        { user_id: userId, new_password: newPassword }
      );
      return response.data;
    },

    // CLAUDE.md §4/§8.11: reemplaza priceLists.assignUsers (retirado) -
    // movido acá 2026-09-04, ahora es "para este usuario, esta es la lista
    // completa de listas de precios (y su tope) a la que puede acceder",
    // en vez de "para esta lista, estos son todos los usuarios asignados".
    assignPriceLists: async (
      id: number,
      assignments: Array<{ price_list_id: number; max_discount_pct?: number | null }>
    ): Promise<User> => {
      const response: AxiosResponse<User> = await axiosInstance.put(
        `/users/${id}/price-list-assignments`, { assignments }
      );
      return response.data;
    },
  };

  // ERP (Bejerman) - "Enviar a ERP" (CLAUDE.md §8). País resuelto del JWT.
  erp = {
    // Registros nativos crudos de Bejerman (claves Cliente_*).
    searchClientes: async (q: string): Promise<any[]> => {
      const response: AxiosResponse<any[]> = await axiosInstance.get('/erp/clientes/search', {
        params: { q }
      });
      return response.data;
    },
    createCliente: async (data: ErpClienteCreateRequest): Promise<any> => {
      const response: AxiosResponse<any> = await axiosInstance.post('/erp/clientes', data);
      return response.data;
    },
    // El submit se encola como job de RQ (SUPERVISOR_NOTES 2026-09-15): devuelve
    // 202 con el handle del job, NO el resultado. El outcome se sondea con
    // getSubmitStatus (persistido en la versión).
    submitBudget: async (
      versionId: number,
      body: {
        cliente_codigo: string;
        include_materiales?: boolean;
        include_mano_obra?: boolean;
        include_flete?: boolean;
      }
    ): Promise<{ job_id: string; erp_submission_status: string }> => {
      const response = await axiosInstance.post(
        `/erp/budgets/${versionId}/submit`, body
      );
      return response.data;
    },
    getSubmitStatus: async (versionId: number): Promise<ErpSubmitStatus> => {
      const response: AxiosResponse<ErpSubmitStatus> = await axiosInstance.get(
        `/erp/budgets/${versionId}/submit/status`
      );
      return response.data;
    },
  };


  // Facturación Mercado Libre (admin + ml_operator).
  ml = {
    listOrders: async (params: {
      status?: string; q?: string; date_from?: string; date_to?: string;
      page?: number; page_size?: number;
    }): Promise<{ total: number; items: MlOrder[] }> => {
      const response = await axiosInstance.get('/ml/orders', { params });
      return response.data;
    },
    oauthStatus: async (): Promise<{ connected: boolean; seller_user_id: string | null }> => {
      const response = await axiosInstance.get('/ml/oauth/status');
      return response.data;
    },
    oauthStart: async (): Promise<{ url: string; state: string }> => {
      const response = await axiosInstance.get('/ml/oauth/start');
      return response.data;
    },
    oauthExchange: async (code: string): Promise<{ connected: boolean }> => {
      const response = await axiosInstance.post('/ml/oauth/exchange', { code });
      return response.data;
    },
  };

  // Endpoints de productos
  products = {
    getAll: async (filters: ProductFilters = {}): Promise<PaginatedResponse<Product>> => {
      const response: AxiosResponse<PaginatedResponse<Product>> = await axiosInstance.get('/products', {
        params: filters
      });
      return response.data;
    },

    getById: async (id: number): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosInstance.get(`/products/${id}`);
      return response.data;
    },

    create: async (productData: ProductFormData): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosInstance.post('/products', productData);
      return response.data;
    },

    update: async (id: number, productData: Partial<ProductFormData>): Promise<Product> => {
      const response: AxiosResponse<Product> = await axiosInstance.put(`/products/${id}`, productData);
      return response.data;
    },

    delete: async (id: number): Promise<void> => {
      await axiosInstance.delete(`/products/${id}`);
    },

    deletePermanently: async (id: number): Promise<{message: string, soft_delete: boolean}> => {
      const response = await axiosInstance.delete(`/products/${id}/permanent`);
      return response.data;
    },

    search: async (query: string): Promise<Product[]> => {
      const response: AxiosResponse<Product[]> = await axiosInstance.get(`/products/search`, {
        params: { q: query }
      });
      return response.data;
    },

    deleteAll: async (): Promise<{ message: string }> => {
      const response: AxiosResponse<{ message: string }> = await axiosInstance.delete('/products/all');
      return response.data;
    },

    uploadPhoto: async (id: number, file: File): Promise<Product> => {
      const formData = new FormData();
      formData.append('file', file);
      const response: AxiosResponse<Product> = await axiosInstance.post(
        `/products/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },

    // CLAUDE.md §7: reemplaza el sync en vivo con Google Sheets retirado
    // para Materiales - carga de archivo CSV/XLS, mismo patrón que
    // priceLists.importExcel.
    importCatalog: async (file: File): Promise<ProductImportResult> => {
      const formData = new FormData();
      formData.append('file', file);
      const response: AxiosResponse<ProductImportResult> = await axiosInstance.post(
        '/products/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },

    // Worker brief "catalog-list-ui-pattern" (2026-09-05): starter CSV for
    // the importer above. Fetched as a Blob (not a plain <a href>) because
    // auth here is a Bearer header on the axios instance, not a cookie - a
    // plain browser-navigated link wouldn't carry it.
    downloadImportTemplate: async (): Promise<Blob> => {
      const response: AxiosResponse<Blob> = await axiosInstance.get('/products/import/template', {
        responseType: 'blob',
      });
      return response.data;
    },

    // CLAUDE.md §7: stock vive en su propia tabla (ProductStock) - único
    // punto de escritura hasta que se defina de dónde viene el dato de stock
    // de ahora en más (input abierto, sin responder todavía).
    updateStock: async (id: number, quantity: number): Promise<{ product_id: number; quantity: number }> => {
      const response = await axiosInstance.put(`/products/${id}/stock`, { quantity });
      return response.data;
    },
  };

  // Fletes: zona x tramo de capacidad (admin, Phase 5 - CLAUDE.md §6/§7)
  freightRates = {
    getAll: async (activeOnly?: boolean): Promise<FreightRate[]> => {
      const response: AxiosResponse<FreightRate[]> = await axiosInstance.get('/freight-rates', {
        params: activeOnly ? { active_only: true } : {}
      });
      return response.data;
    },

    create: async (data: FreightRateFormData): Promise<FreightRate> => {
      const response: AxiosResponse<FreightRate> = await axiosInstance.post('/freight-rates', data);
      return response.data;
    },

    update: async (id: number, data: Partial<FreightRateFormData>): Promise<FreightRate> => {
      const response: AxiosResponse<FreightRate> = await axiosInstance.put(`/freight-rates/${id}`, data);
      return response.data;
    },

    delete: async (id: number): Promise<{ message: string }> => {
      const response = await axiosInstance.delete(`/freight-rates/${id}`);
      return response.data;
    },

    importFile: async (file: File): Promise<any> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post('/freight-rates/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },

    // Worker brief "catalog-list-ui-pattern" (2026-09-05), continued to
    // Fletes: starter CSV for the importer above. Fetched as a Blob (not a
    // plain <a href>) for the same reason as products.downloadImportTemplate
    // above - auth here is a Bearer header on the axios instance, not a
    // cookie.
    downloadImportTemplate: async (): Promise<Blob> => {
      const response: AxiosResponse<Blob> = await axiosInstance.get('/freight-rates/import/template', {
        responseType: 'blob',
      });
      return response.data;
    },
  };

  // Mano de Obra: catálogo de servicios de instalación (admin - CLAUDE.md
  // §7/§8.5). `update` respalda la edición inline de InstallationList.tsx -
  // PUT /installation-services/{id} ya existía en el backend, solo faltaba
  // este wrapper (la lista usaba apiService.get/post genéricos hasta ahora).
  installationServices = {
    update: async (id: number, data: Partial<InstallationServiceCreate>): Promise<InstallationService> => {
      const response: AxiosResponse<InstallationService> = await axiosInstance.put(
        `/installation-services/${id}`, data
      );
      return response.data;
    },

    // Worker brief "catalog-list-ui-pattern" (2026-09-05), same pattern as
    // products.downloadImportTemplate above - Blob fetch (not a plain <a
    // href>) because auth is a Bearer header on the axios instance.
    downloadImportTemplate: async (): Promise<Blob> => {
      const response: AxiosResponse<Blob> = await axiosInstance.get('/installation-services/import/template', {
        responseType: 'blob',
      });
      return response.data;
    },
  };

  // Reportes de conciliación de importaciones de catálogo (Phase 5 - CLAUDE.md §7)
  catalogImportRuns = {
    getAll: async (kind?: string, limit?: number): Promise<CatalogImportRun[]> => {
      const response: AxiosResponse<CatalogImportRun[]> = await axiosInstance.get('/catalog-import-runs', {
        params: { ...(kind ? { kind } : {}), ...(limit ? { limit } : {}) }
      });
      return response.data;
    },

    getById: async (id: number): Promise<CatalogImportRun> => {
      const response: AxiosResponse<CatalogImportRun> = await axiosInstance.get(`/catalog-import-runs/${id}`);
      return response.data;
    },
  };

  // Migración de fotos de catálogo, Drive -> GCS (Phase 5 - CLAUDE.md §7, no es por país)
  catalogPhotos = {
    import: async (folderId?: string, upload: boolean = true): Promise<any> => {
      const response = await axiosInstance.post('/catalog-photos/import', null, {
        params: { ...(folderId ? { folder_id: folderId } : {}), upload }
      });
      return response.data;
    },
  };

  // Listas de precios (admin, Phase 4 - CLAUDE.md §4)
  priceLists = {
    getAll: async (activeOnly?: boolean): Promise<PriceList[]> => {
      const response: AxiosResponse<PriceList[]> = await axiosInstance.get('/price-lists', {
        params: activeOnly ? { active_only: true } : {}
      });
      return response.data;
    },

    getMine: async (): Promise<PriceList[]> => {
      const response: AxiosResponse<PriceList[]> = await axiosInstance.get('/price-lists/mine');
      return response.data;
    },

    getById: async (id: number): Promise<PriceList & { entries: PriceListEntry[] }> => {
      const response = await axiosInstance.get(`/price-lists/${id}`);
      return response.data;
    },

    create: async (name: string): Promise<PriceList> => {
      const response: AxiosResponse<PriceList> = await axiosInstance.post('/price-lists', { name });
      return response.data;
    },

    update: async (id: number, data: { name?: string; is_active?: boolean }): Promise<PriceList> => {
      const response: AxiosResponse<PriceList> = await axiosInstance.put(`/price-lists/${id}`, data);
      return response.data;
    },

    delete: async (id: number): Promise<{ message: string }> => {
      const response = await axiosInstance.delete(`/price-lists/${id}`);
      return response.data;
    },

    getEntries: async (id: number): Promise<PriceListEntry[]> => {
      const response: AxiosResponse<PriceListEntry[]> = await axiosInstance.get(`/price-lists/${id}/entries`);
      return response.data;
    },

    createEntry: async (id: number, data: PriceListEntryFormData): Promise<PriceListEntryResult> => {
      const response: AxiosResponse<PriceListEntryResult> = await axiosInstance.post(`/price-lists/${id}/entries`, data);
      return response.data;
    },

    updateEntry: async (
      id: number, entryId: number, data: Partial<PriceListEntryFormData>
    ): Promise<PriceListEntryResult> => {
      const response: AxiosResponse<PriceListEntryResult> = await axiosInstance.put(
        `/price-lists/${id}/entries/${entryId}`, data
      );
      return response.data;
    },

    deleteEntry: async (id: number, entryId: number): Promise<{ message: string }> => {
      const response = await axiosInstance.delete(`/price-lists/${id}/entries/${entryId}`);
      return response.data;
    },

    getPrices: async (id: number): Promise<Record<number, { price: number; descuento: number }>> => {
      const response = await axiosInstance.get(`/price-lists/${id}/prices`);
      return response.data;
    },

    importExcel: async (id: number, file: File): Promise<PriceListImportResult> => {
      const formData = new FormData();
      formData.append('file', file);
      const response: AxiosResponse<PriceListImportResult> = await axiosInstance.post(
        `/price-lists/${id}/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },

    // "Productos sin precio en esta lista" (CLAUDE.md §4) - checkable
    // anytime, not just right after an upload.
    getMissingProducts: async (id: number): Promise<Array<{ id: number; codigo: string; descripcion?: string }>> => {
      const response = await axiosInstance.get(`/price-lists/${id}/missing-products`);
      return response.data;
    },

    // Worker brief "catalog-list-ui-pattern" (2026-09-05) - same
    // authenticated-blob-download pattern as products.downloadImportTemplate,
    // since the endpoint requires the bearer token (a plain <a href> link
    // wouldn't carry it).
    downloadImportTemplate: async (): Promise<Blob> => {
      const response: AxiosResponse<Blob> = await axiosInstance.get('/price-lists/import/template', {
        responseType: 'blob',
      });
      return response.data;
    },
  };

  // Endpoints de presupuestos
  budgets = {
  createVersion: this.createVersion,
    /**
     * Preview de cálculo de ítems de presupuesto (centralizado en backend)
     * @param items Lista de items a calcular (product_id, metros_cuadrados, con_desperdicio, con_estructura, ...)
     * @returns Lista de items calculados (con cantidad, subtotal, estructura, etc)
     */
    previewCalculation: async (items: Array<{
      product_id: number;
      metros_cuadrados: number;
      con_desperdicio?: boolean;
      con_estructura?: boolean;
      con_terminaciones?: boolean;
      con_instalacion?: boolean;
      is_structure?: boolean;
    }>): Promise<any[]> => {
      const response: AxiosResponse<any[]> = await axiosInstance.post('/budget/preview', { items });
      return response.data;
    },
    getAll: async (filters: BudgetFilters = {}): Promise<PaginatedResponse<Budget>> => {
      const response: AxiosResponse<PaginatedResponse<Budget>> = await axiosInstance.get('/budgets', {
        params: filters
      });
      return response.data;
    },

    getById: async (id: number): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.get(`/budgets/${id}`);
      return response.data;
    },

    create: async (budgetData: BudgetFormData): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.post('/budgets', budgetData);
      return response.data;
    },

    update: async (id: number, versionData: any): Promise<BudgetVersion> => {
      const response: AxiosResponse<BudgetVersion> = await axiosInstance.put(`/budgets/${id}`, versionData);
      return response.data;
    },

    updateClientInfo: async (id: number, clientData: { client_name: string; client_email: string }): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.patch(`/budgets/${id}/client`, clientData);
      return response.data;
    },

    delete: async (id: number): Promise<void> => {
      await axiosInstance.delete(`/budgets/${id}`);
    },

    duplicate: async (id: number): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.post(`/budgets/${id}/duplicate`);
      return response.data;
    },

    addItem: async (id: number, item: { 
      product_id: number; 
      metros_cuadrados: number;
      con_desperdicio?: boolean;
      con_estructura?: boolean;
    }): Promise<Budget> => {
      // En lugar de agregar un item directamente, creamos una nueva versión con el item agregado
      // Primero obtenemos el budget actual
      const currentBudget = await axiosInstance.get(`/budgets/${id}`);
      const budget = currentBudget.data;
      
      // Obtenemos la versión actual
      const currentVersion = budget.versions?.find((v: any) => v.is_current) || budget.versions?.[0];
      
      if (!currentVersion) {
        throw new Error('No se encontró versión actual del presupuesto');
      }
      
      // Creamos una nueva versión con todos los items existentes más el nuevo
      const newVersionData = {
        title: currentVersion.title,
        notes: currentVersion.notes || '',
        vendor_name: currentVersion.vendor_name,
        vendor_email: currentVersion.vendor_email,
        sections_count: currentVersion.sections_count || 1,
        items: [
          ...(currentVersion.items || []).map((existingItem: any) => ({
            product_id: existingItem.product_id,
            metros_cuadrados: existingItem.metros_cuadrados,
            con_desperdicio: existingItem.con_desperdicio || false,
            con_estructura: existingItem.con_estructura || false,
            con_terminaciones: existingItem.con_terminaciones || false,
            con_instalacion: existingItem.con_instalacion || false,
            order_index: existingItem.order_index || 0,
            is_structure: existingItem.is_structure === true // Si ya viene marcado, lo respeta
          })),
          {
            product_id: item.product_id,
            metros_cuadrados: item.metros_cuadrados,
            con_desperdicio: item.con_desperdicio || false,
            con_estructura: item.con_estructura || false,
            con_terminaciones: false,
            con_instalacion: false,
            order_index: (currentVersion.items?.length || 0),
            is_structure: false // El ítem principal siempre explícitamente
          }
        ]
      };
      
      const response: AxiosResponse<any> = await axiosInstance.post(`/budgets/${id}/versions`, newVersionData);
      
      // Después de crear la nueva versión, obtenemos el budget actualizado
      const updatedBudgetResponse: AxiosResponse<Budget> = await axiosInstance.get(`/budgets/${id}`);
      return updatedBudgetResponse.data;
    },

    removeItem: async (budgetId: number, itemId: number): Promise<Budget> => {
      // Para eliminar un item, creamos una nueva versión sin ese item
      // Primero obtenemos el budget actual
      const currentBudget = await axiosInstance.get(`/budgets/${budgetId}`);
      const budget = currentBudget.data;
      
      // Obtenemos la versión actual
      const currentVersion = budget.versions?.find((v: any) => v.is_current) || budget.versions?.[0];
      
      if (!currentVersion) {
        throw new Error('No se encontró versión actual del presupuesto');
      }
      
      // Filtramos los items excluyendo el que queremos eliminar
      const filteredItems = (currentVersion.items || [])
        .filter((existingItem: any) => existingItem.id !== itemId)
        .map((existingItem: any) => ({
          product_id: existingItem.product_id,
          metros_cuadrados: existingItem.metros_cuadrados,
          cantidad: existingItem.cantidad,
          con_desperdicio: existingItem.con_desperdicio || false,
          con_estructura: existingItem.con_estructura || false,
          con_terminaciones: existingItem.con_terminaciones || false,
          con_instalacion: existingItem.con_instalacion || false,
          order_index: existingItem.order_index || 0
        }));
      
      // Creamos una nueva versión sin el item eliminado
      const newVersionData = {
        title: currentVersion.title,
        notes: currentVersion.notes || '',
        vendor_name: currentVersion.vendor_name,
        vendor_email: currentVersion.vendor_email,
        sections_count: currentVersion.sections_count || 1,
        items: filteredItems
      };
      
      const response: AxiosResponse<any> = await axiosInstance.post(`/budgets/${budgetId}/versions`, newVersionData);
      
      // Después de crear la nueva versión, obtenemos el budget actualizado
      const updatedBudgetResponse: AxiosResponse<Budget> = await axiosInstance.get(`/budgets/${budgetId}`);
      return updatedBudgetResponse.data;
    },

    addStructureItem: async (id: number, item: {
      descripcion: string;
      unidad: string;
      cantidad: number;
      precio_unitario: number;
    }): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.post(`/budgets/${id}/structure-items`, item);
      return response.data;
    },

    removeStructureItem: async (budgetId: number, itemId: number): Promise<Budget> => {
      const response: AxiosResponse<Budget> = await axiosInstance.delete(`/budgets/${budgetId}/structure-items/${itemId}`);
      return response.data;
    },

    updateStatus: async (id: number, status: string): Promise<Budget> => {
  const response: AxiosResponse<Budget> = await axiosInstance.patch(`/budgets/${id}/status`, { status });
  return response.data;
    },

    exportPdf: async (id: number, versionId?: number): Promise<Blob> => {
      const url = versionId 
        ? `/budgets/${id}/export/pdf?version_id=${versionId}`
        : `/budgets/${id}/export/pdf`;
      const response: AxiosResponse<Blob> = await axiosInstance.get(url, {
        responseType: 'blob'
      });
      return response.data;
    },

    exportExcel: async (startDate: string, endDate: string, status?: string): Promise<Blob> => {
      const response: AxiosResponse<Blob> = await axiosInstance.get('/budgets/export/excel', {
        params: { start_date: startDate, end_date: endDate, status: status || undefined },
        responseType: 'blob'
      });
      return response.data;
    },

    getStats: async () => {
      const response = await axiosInstance.get('/budgets/stats');
      return response.data;
    },

    getAdvancedStats: async () => {
      const response = await axiosInstance.get('/budgets/advanced-stats');
      return response.data;
    },

    getUserActivityStats: async (limit: number = 10) => {
      const response = await axiosInstance.get('/budgets/user-activity-stats', {
        params: { limit }
      });
      return response.data;
    },

    getUserSalesStats: async (limit: number = 10) => {
      const response = await axiosInstance.get('/budgets/user-sales-stats', {
        params: { limit }
      });
      return response.data;
    },
  };
}

// Instancia singleton del servicio
export const apiService = new ApiService();

// Exportar axiosInstance para uso compartido
export { axiosInstance };

// Configurar token al inicializar si existe en localStorage
const token = localStorage.getItem('token');
if (token) {
  apiService.setAuthToken(token);
}

export default apiService;
